import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Text from '@/components/common/Text';
import { Theme } from '@/styles/Theme';
import styled from '@emotion/styled';
import type { MonthlyBillDetail } from '@/types/admin.types';
import {
  confirmMonthlyBillPayment,
  blockUser,
} from '@/apis/admin';
import { QUERY_KEY } from '@/constants/queryKey';
import { ROUTER } from '@/constants/constants';

interface ActionsPanelProps {
  bill: MonthlyBillDetail;
}

const ActionsPanel = ({ bill }: ActionsPanelProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');

  const approveMutation = useMutation({
    mutationFn: (data: { action: 'approve'; note?: string }) =>
      confirmMonthlyBillPayment(bill.id, data),
    onSuccess: () => {
      toast.success('결제가 승인되었습니다.');
      queryClient.invalidateQueries([QUERY_KEY.ADMIN.MONTHLY_BILLS]);
      queryClient.invalidateQueries([QUERY_KEY.ADMIN.MONTHLY_BILL_DETAIL, bill.id]);
      setShowApproveModal(false);
      setNote('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '승인 실패');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (data: { action: 'reject'; note: string }) =>
      confirmMonthlyBillPayment(bill.id, data),
    onSuccess: () => {
      toast.success('결제가 거부되었습니다.');
      queryClient.invalidateQueries([QUERY_KEY.ADMIN.MONTHLY_BILLS]);
      queryClient.invalidateQueries([QUERY_KEY.ADMIN.MONTHLY_BILL_DETAIL, bill.id]);
      setShowRejectModal(false);
      setNote('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '거부 실패');
    },
  });

  const blockMutation = useMutation({
    mutationFn: (data: { reason: string }) => blockUser(bill.user_id, data),
    onSuccess: () => {
      toast.success('사용자가 차단되었습니다.');
      queryClient.invalidateQueries([QUERY_KEY.ADMIN.MONTHLY_BILL_DETAIL, bill.id]);
      setShowBlockModal(false);
      setReason('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '차단 실패');
    },
  });

  const handleApprove = () => {
    if (!note.trim()) {
      approveMutation.mutate({ action: 'approve' });
    } else {
      approveMutation.mutate({ action: 'approve', note: note.trim() });
    }
  };

  const handleReject = () => {
    if (!note.trim()) {
      toast.error('거부 사유를 입력해주세요.');
      return;
    }
    rejectMutation.mutate({ action: 'reject', note: note.trim() });
  };

  const handleBlock = () => {
    if (!reason.trim()) {
      toast.error('차단 사유를 입력해주세요.');
      return;
    }
    blockMutation.mutate({ reason: reason.trim() });
  };

  const canApprove = bill.status === 'pending_confirmation' || bill.status === 'sent';
  const canReject = bill.status === 'pending_confirmation';
  const canBlock = (bill.status === 'overdue' || bill.status === 'rejected') && bill.user;

  return (
    <>
      <Container>
        <Header>
          <Text fontSize='1.25rem' fontWeight={700}>
            작업
          </Text>
        </Header>
        <ButtonRow>
          {canApprove && (
            <ActionButton
              variant='approve'
              onClick={() => setShowApproveModal(true)}
            >
              결제 승인
            </ActionButton>
          )}
          {canReject && (
            <ActionButton
              variant='reject'
              onClick={() => setShowRejectModal(true)}
            >
              결제 거부
            </ActionButton>
          )}
          {canBlock && (
            <ActionButton
              variant='block'
              onClick={() => setShowBlockModal(true)}
            >
              사용자 차단
            </ActionButton>
          )}
          {bill.user && (
            <ActionButton
              variant='secondary'
              onClick={() => navigate(`${ROUTER.ADMIN.USER_MANAGE}/${bill.user!.id}`)}
            >
              사용자 보기
            </ActionButton>
          )}
        </ButtonRow>
      </Container>

      {/* Approve Modal */}
      {showApproveModal && (
        <ModalOverlay onClick={() => setShowApproveModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>결제 승인</ModalTitle>
            <ModalText>청구서 #{bill.id}의 결제를 승인하시겠습니까?</ModalText>
            <Textarea
              placeholder='관리자 메모 (선택사항)'
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <ModalButtonRow>
              <ModalButton
                backgroundColor={Theme.colors.G_3}
                onClick={() => {
                  setShowApproveModal(false);
                  setNote('');
                }}
              >
                취소
              </ModalButton>
              <ModalButton
                backgroundColor={Theme.colors.B_1}
                onClick={handleApprove}
                disabled={approveMutation.isLoading}
              >
                {approveMutation.isLoading ? '처리 중...' : '승인'}
              </ModalButton>
            </ModalButtonRow>
          </Modal>
        </ModalOverlay>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <ModalOverlay onClick={() => setShowRejectModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>결제 거부</ModalTitle>
            <ModalText>청구서 #{bill.id}의 결제를 거부하시겠습니까?</ModalText>
            <Textarea
              placeholder='거부 사유 (필수)'
              value={note}
              onChange={(e) => setNote(e.target.value)}
              required
            />
            <ModalButtonRow>
              <ModalButton
                backgroundColor={Theme.colors.G_3}
                onClick={() => {
                  setShowRejectModal(false);
                  setNote('');
                }}
              >
                취소
              </ModalButton>
              <ModalButton
                backgroundColor={Theme.colors.WARNING_RED}
                onClick={handleReject}
                disabled={rejectMutation.isLoading}
              >
                {rejectMutation.isLoading ? '처리 중...' : '거부'}
              </ModalButton>
            </ModalButtonRow>
          </Modal>
        </ModalOverlay>
      )}

      {/* Block Modal */}
      {showBlockModal && (
        <ModalOverlay onClick={() => setShowBlockModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>사용자 차단</ModalTitle>
            <ModalText>
              {bill.user?.company} 사용자를 차단하시겠습니까?
              <br />
              사용자의 구독 상태가 '만료'로 변경됩니다.
            </ModalText>
            <Textarea
              placeholder='차단 사유 (필수)'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
            <ModalButtonRow>
              <ModalButton
                backgroundColor={Theme.colors.G_3}
                onClick={() => {
                  setShowBlockModal(false);
                  setReason('');
                }}
              >
                취소
              </ModalButton>
              <ModalButton
                backgroundColor={Theme.colors.WARNING_RED}
                onClick={handleBlock}
                disabled={blockMutation.isLoading}
              >
                {blockMutation.isLoading ? '처리 중...' : '차단'}
              </ModalButton>
            </ModalButtonRow>
          </Modal>
        </ModalOverlay>
      )}
    </>
  );
};

const Container = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 0.0625rem solid ${Theme.colors.G_4};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const ActionButton = styled.button<{ variant: 'approve' | 'reject' | 'block' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  min-width: 8rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s ease-in-out;
  
  ${(props) => {
    switch (props.variant) {
      case 'approve':
        return `
          background-color: ${Theme.colors.B_3};
          color: white;
          &:hover {
            background-color: ${Theme.colors.B_2};
            transform: translateY(-0.0625rem);
            box-shadow: 0 0.25rem 0.5rem rgba(19, 127, 218, 0.3);
          }
        `;
      case 'reject':
        return `
          background-color: ${Theme.colors.WARNING_RED};
          color: white;
          &:hover {
            background-color: #E02E3E;
            transform: translateY(-0.0625rem);
            box-shadow: 0 0.25rem 0.5rem rgba(246, 55, 75, 0.3);
          }
        `;
      case 'block':
        return `
          background-color: ${Theme.colors.WARNING_RED};
          color: white;
          &:hover {
            background-color: #E02E3E;
            transform: translateY(-0.0625rem);
            box-shadow: 0 0.25rem 0.5rem rgba(246, 55, 75, 0.3);
          }
        `;
      case 'secondary':
        return `
          background-color: white;
          color: ${Theme.colors.G_1};
          border: 0.0625rem solid ${Theme.colors.G_3};
          &:hover {
            background-color: ${Theme.colors.G_6};
            border-color: ${Theme.colors.G_2};
            transform: translateY(-0.0625rem);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:active {
    transform: translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem;
  min-width: 25rem;
  max-width: 90vw;
`;

const ModalTitle = styled(Text)`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const ModalText = styled(Text)`
  font-size: 0.875rem;
  color: ${Theme.colors.G_1};
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 6rem;
  padding: 0.75rem;
  border: 0.0625rem solid ${Theme.colors.G_3};
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 1.5rem;

  &:focus {
    outline: none;
    border-color: ${Theme.colors.B_1};
  }
`;

const ModalButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ backgroundColor: string }>`
  padding: 0.625rem 1.25rem;
  background-color: ${(props) => props.backgroundColor};
  color: white;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default ActionsPanel;

